import { useEffect, useRef, useState } from 'react';
import './styles.scss';

/**
 * LazyImage — wrapper around <img> with lazy loading via IntersectionObserver.
 * The image is requested only when it enters the viewport (with optional rootMargin offset).
 * In browsers without IntersectionObserver (IE11, older Safari) the image loads immediately.
 *
 * @param {string} src — Image URL.
 * @param {string} [alt] — Alt text for the img.
 * @param {string} [className] — CSS class applied to the img element.
 * @param {string} [placeholder] — Optional placeholder image URL until the main image loads.
 * @param {string} [rootMargin='100px'] — Load trigger offset (IntersectionObserver format: '100px', '50px 0', etc.).
 * @param {object} [imgProps] — Remaining props are passed to <img> (width, height, sizes, etc.).
 */
export const LazyImg = ({
    src,
    alt = '',
    className = '',
    placeholder,
    rootMargin = '100px',
    ...imgProps
}) => {
    const imgRef = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const img = imgRef.current;
        if (!img || !src) return;

        if (typeof IntersectionObserver === 'undefined') {
            setIsInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setIsInView(true);
                });
            },
            { rootMargin, threshold: 0.01 }
        );

        observer.observe(img);
        return () => observer.disconnect();
    }, [src, rootMargin]);

    const effectiveSrc = isInView ? src : (placeholder ?? undefined);

    return (
        <img
            ref={imgRef}
            alt={alt}
            src={effectiveSrc}
            className={`lazy-image ${isLoaded ? 'lazy-image--loaded' : ''} ${className}`.trim()}
            onLoad={isInView ? () => setIsLoaded(true) : undefined}
            {...imgProps}
        />
    );
};

export default LazyImg;
